import { createFileRoute, notFound } from "@tanstack/react-router";

import { View } from "@/booking/View";
import Footer from "@/components/Footer";
import GdprConsent from "@/landing/components/GdprConsent";
import { getStoreInfoByIdFn } from "@/server-fns/booking/serverFns";
import { toSafeJsonLdString } from "@/utils/safeJsonLd";

const SITE_URL = "https://diningmate.com.au";
const FALLBACK_OG_IMAGE = `${SITE_URL}/og-image.png`;

export const Route = createFileRoute("/$storeId/$slug")({
    validateSearch: (search): { mode?: "manual" } => ({
        mode: search.mode === "manual" ? "manual" : undefined,
    }),
    loader: async ({ params }) => {
        const storeData = await getStoreInfoByIdFn({
            data: { storeId: params.storeId.toUpperCase() },
        });
        if (!storeData) {
            throw notFound();
        }
        return { storeData };
    },
    head: ({ loaderData, params }) => {
        const store = loaderData?.storeData;
        if (!store) return {};

        const storeId = params.storeId.toUpperCase();
        const url = `${SITE_URL}/${storeId}/${store.slug}`;
        const title = `Book at ${store.name} | DiningMate`;
        const formattedAddress = `${store.streetAddress}, ${store.suburb} ${store.state} ${store.postcode}`;
        const description = `Reserve a table at ${store.name}, ${formattedAddress}. Book online instantly — no phone call needed.`;
        const ogImage = store.imageUrl ?? FALLBACK_OG_IMAGE;

        const restaurantSchema = {
            "@context": "https://schema.org",
            "@type": "Restaurant",
            name: store.name,
            address: formattedAddress,
            telephone: store.phone,
            url,
            image: ogImage,
            reservations: "Required",
            potentialAction: {
                "@type": "ReserveAction",
                target: url,
            },
        };

        return {
            meta: [
                { title },
                { name: "description", content: description },
                { property: "og:type", content: "website" },
                { property: "og:url", content: url },
                { property: "og:title", content: title },
                { property: "og:description", content: description },
                { property: "og:image", content: ogImage },
                { property: "og:image:width", content: "1200" },
                { property: "og:image:height", content: "630" },
                { name: "twitter:card", content: "summary_large_image" },
                { name: "twitter:title", content: title },
                { name: "twitter:description", content: description },
                { name: "twitter:image", content: ogImage },
            ],
            links: [{ rel: "canonical", href: url }],
            scripts: [
                {
                    type: "application/ld+json",
                    children: toSafeJsonLdString(restaurantSchema),
                },
            ],
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    const { storeId } = Route.useParams();
    const { storeData } = Route.useLoaderData();
    const normalizedStoreId = storeId.toUpperCase();

    return (
        <>
            <View storeId={normalizedStoreId} storeData={storeData} />
            <Footer />
            <GdprConsent />
        </>
    );
}
